<template>
  <div class="davisson-germer-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$λ = h / √(2meV)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>加速电压 (V_acc)</label>
            <span class="val">{{ voltage }} <small>V</small></span>
          </div>
          <div class="hud-item orange">
            <label>德布罗意波长 (λ_e)</label>
            <span class="val">{{ electronWavelength.toFixed(3) }} <small>nm</small></span>
          </div>
        </div>

        <svg width="100%" height="260" viewBox="0 0 450 260" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-e-wave"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <radialGradient id="atom-grad"><stop offset="0%" stop-color="#94a3b8" /><stop offset="100%" stop-color="#1e293b" /></radialGradient>
          </defs>

          <!-- 电子枪 (Electron Gun) -->
          <rect x="20" y="110" width="60" height="20" fill="#334155" rx="2" />
          <path d="M 80,120 L 150,120" stroke="#0ea5e9" stroke-width="2" stroke-dasharray="4,2" opacity="0.6" />

          <!-- 镍单晶原子点阵 (Nickel Crystal) -->
          <g transform="translate(180, 80)">
             <circle v-for="i in 3" :key="'a1'+i" :cx="i*25" cy="0" r="6" fill="url(#atom-grad)" />
             <circle v-for="i in 3" :key="'a2'+i" :cx="i*25" cy="25" r="6" fill="url(#atom-grad)" />
             <circle v-for="i in 3" :key="'a3'+i" :cx="i*25" cy="50" r="6" fill="url(#atom-grad)" />
             <text x="35" y="80" text-anchor="middle" fill="#475569" font-size="9" font-weight="900">镍晶面 (Target)</text>
          </g>

          <!-- 探测轨迹与衍射波 (Diffracted Wave) -->
          <g v-if="isRunning">
             <!-- 入射波束 -->
             <line x1="80" y1="120" x2="200" y2="120" stroke="#0ea5e9" stroke-width="2" filter="url(#glow-e-wave)" />
             
             <!-- 衍射散射 (角度 Φ) -->
             <line x1="200" y1="120" 
                   :x2="200 + Math.cos(targetAngle * Math.PI / 180) * 120" 
                   :y2="120 - Math.sin(targetAngle * Math.PI / 180) * 120" 
                   stroke="#fbbf24" stroke-width="4" :opacity="interferenceIntensity / 100" filter="url(#glow-e-wave)" />
          </g>

          <!-- 法拉第杯探测器 (Movable Collector) -->
          <g :transform="`translate(${200 + Math.cos(detectorAngle * Math.PI / 180) * 130}, ${120 - Math.sin(detectorAngle * Math.PI / 180) * 130}) rotate(${-detectorAngle})`">
             <rect x="-10" y="-15" width="20" height="30" fill="#1e293b" stroke="#94a3b8" />
             <text x="25" y="5" fill="#94a3b8" font-size="8">探测器 (Collector)</text>
          </g>

          <!-- 背景角度盘 -->
          <circle cx="200" cy="120" r="130" fill="none" stroke="#1e293b" stroke-dasharray="2,4" />
        </svg>

        <div class="intensity-chart">
           <div class="chart-label">极轴散射强度图 (Intensity Profile)</div>
           <!-- 这里精准展示：54V时，50度角出现峰值 -->
           <div class="peak-msg" v-if="isPeakActive">观测到明显的布喇格衍射峰 (Diffraction Peak)</div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row">
              <div class="control-group">
                 <label class="s-label">加速电压 (调节λ): {{ voltage }}V</label>
                 <el-slider v-model="voltage" :min="30" :max="80" size="small"></el-slider>
              </div>
              <div class="control-group">
                 <label class="s-label">探测器偏转角度 (Φ): {{ detectorAngle }}°</label>
                 <el-slider v-model="detectorAngle" :min="0" :max="90" size="small"></el-slider>
              </div>
              <div class="btn-group">
                 <el-button type="primary" size="small" @click="isRunning = !isRunning">{{ isRunning ? '切断电子束' : '开启电子束' }}</el-button>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-view"></i>
           <p><strong>实战物理：</strong>电子不仅是粒子，也是波。<strong>实验发现：</strong>当电压调至 54V 时，你会发现探测器在 50° 位置处接收到的信号强度陡增。这说明电子在晶面间发生了类似于 X 射线的<strong>相干衍射</strong>。这不仅证明了德布罗意假设，更为后来的电子显微镜技术奠定了理论基础。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'DavissonGermerViz',
  data() {
    return {
      voltage: 54,
      detectorAngle: 30,
      targetAngle: 50,
      isRunning: true
    }
  },
  computed: {
    electronWavelength() {
       // lambda = h / sqrt(2meV) approx 1.226 / sqrt(V) nm
       return 1.226 / Math.sqrt(this.voltage);
    },
    interferenceIntensity() {
       // Mock intensity: peak at 54V and 50deg
       const voltageDist = Math.abs(this.voltage - 54);
       const angleDist = Math.abs(this.detectorAngle - 50);
       return 100 * Math.exp(-(voltageDist * 0.1 + angleDist * 0.05));
    },
    isPeakActive() {
       return this.interferenceIntensity > 60;
    }
  }
}
</script>

<style scoped lang="scss">
.davisson-germer-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #166534; border: 1px solid #dcfce7; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 260px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #0ea5e9; } &.orange { border-left: 3px solid #f97316; } } }
.intensity-chart { position: absolute; bottom: 12px; left: 12px; padding: 6px 12px; background: rgba(15,23,42,0.6); border-radius: 6px; .chart-label { font-size: 9px; color: #64748b; font-weight: 900; } .peak-msg { font-size: 11px; color: #fbbf24; font-weight: bold; margin-top: 2px; } }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; .flex-row { display: flex; gap: 15px; align-items: flex-end; .control-group { flex: 1; .s-label { font-size: 10px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; } } .btn-group { display: flex; gap: 8px; } } }
.discovery-card-compact { background: #f0fdf4; padding: 10px 14px; border-radius: 10px; border: 1px solid #dcfce7; display: flex; align-items: center; gap: 10px; i { color: #16a34a; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 11px; color: #166534; line-height: 1.5; strong { color: #14532d; } } }
</style>
